import theme from '../../theme';
import { calculateResults, DEMAND_TOTAL, SLIDER_MAX } from './goods';

const TABLE_HEADERS = ['Good', 'You planned', 'Demand qty', 'Gap', 'Surplus / Shortfall'];

// ── Sub-components ────────────────────────────────────────────────────────────

const ScoreCircle = ({ score }) => (
  <>
    <div style={{
      width: '90px', height: '90px', borderRadius: '50%',
      border: `3px solid ${theme.gold}`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      margin: '1rem auto',
    }}>
      <span style={{ fontSize: '32px', fontWeight: '600', color: theme.gold }}>{score}</span>
      <span style={{ fontSize: '12px', color: theme.textMuted }}>/100</span>
    </div>
    <p style={{ textAlign: 'center', fontSize: '12px', color: theme.textMuted, marginBottom: '1.5rem' }}>
      planning score
    </p>
  </>
);

const ResultsTable = ({ rows, total, totalShortfall, totalSurplus }) => (
  <>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '1.5rem' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid rgba(184, 149, 90, 0.2)' }}>
          {TABLE_HEADERS.map(h => (
            <th key={h} style={{
              padding: '4px 8px',
              textAlign: h === 'Good' ? 'left' : 'right',
              fontWeight: '500', fontSize: '12px', color: theme.textMuted,
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: '1px solid rgba(184, 149, 90, 0.1)' }}>
            <td style={{ padding: '7px 8px', fontWeight: '500', color: theme.gold }}>{r.name}</td>
            <td style={{ padding: '7px 8px', textAlign: 'right' }}>{r.qty}</td>
            <td style={{ padding: '7px 8px', textAlign: 'right' }}>{r.demandQty}</td>
            <td style={{
              padding: '7px 8px', textAlign: 'right', fontWeight: '500',
              color: r.gap > 0 ? '#df8b3a' : r.gap < 0 ? theme.error : theme.success,
            }}>
              {r.gap > 0 ? `+${r.gap}` : r.gap}
            </td>
            <td style={{
              padding: '7px 8px', textAlign: 'right', fontSize: '12px',
              color: r.gap > 0 ? '#df8b3a' : r.gap < 0 ? theme.error : theme.success,
            }}>
              {r.gap > 0 ? `${r.gap} surplus` : r.gap < 0 ? `${Math.abs(r.gap)} shortfall` : 'balanced'}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr style={{ fontSize: '12px', color: theme.textMuted }}>
          <td style={{ padding: '8px 8px 0' }}>Total</td>
          <td style={{ padding: '8px 8px 0', textAlign: 'right' }}>{total}</td>
          <td style={{ padding: '8px 8px 0', textAlign: 'right' }}>{DEMAND_TOTAL}</td>
          <td></td>
          <td style={{ padding: '8px 8px 0', textAlign: 'right' }}>
            <span style={{ color: '#df8b3a' }}>{totalSurplus} surplus</span>
            {' · '}
            <span style={{ color: theme.error }}>{totalShortfall} shortfall</span>
          </td>
        </tr>
      </tfoot>
    </table>
    <p style={{ fontSize: '12px', color: theme.textMuted, marginTop: '-1rem', marginBottom: '1.5rem' }}>
      Demand qty = the fixed quantity of each good this scenario's consumers sought. Your total
      doesn't have to match it, but falling short of demand or producing unwanted surplus both cost points.
    </p>
  </>
);

const BarChart = ({ rows }) => (
  <>
    <p style={{ fontSize: '12px', fontWeight: '500', color: theme.textMuted, margin: '1.5rem 0 0.75rem' }}>
      Planned quantity vs. demand quantity
    </p>
    {rows.map((r, i) => {
      const yourPct = Math.round((r.qty / SLIDER_MAX) * 100);
      const demPct  = Math.round((r.demandQty / SLIDER_MAX) * 100);
      return (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '10px 0', borderBottom: '0.5px solid rgba(184, 149, 90, 0.2)',
        }}>
          <span style={{ width: '80px', fontSize: '14px', fontWeight: '500', color: theme.textPrimary, flexShrink: 0 }}>
            {r.name}
          </span>
          <div style={{ flex: 1, height: '10px', background: theme.bgDark, borderRadius: '2px', position: 'relative', overflow: 'visible' }}>
            <div style={{ height: '100%', borderRadius: '2px', background: theme.gold, width: `${yourPct}%`, transition: 'width 0.6s' }} />
            <div style={{
              position: 'absolute', top: 0, height: '100%',
              borderRadius: '2px', border: `2px solid ${theme.error}`,
              width: `${demPct}%`, background: 'transparent', transition: 'width 0.6s',
            }} />
          </div>
          <div style={{ width: '80px', textAlign: 'right', flexShrink: 0 }}>
            <span style={{ fontSize: '14px', fontWeight: '500', color: theme.gold }}>{r.qty}</span>
            <span style={{ fontSize: '13px', color: theme.textMuted, margin: '0 2px' }}>/</span>
            <span style={{ fontSize: '14px', fontWeight: '500', color: theme.error }}>{r.demandQty}</span>
          </div>
        </div>
      );
    })}
    <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: theme.textMuted, margin: '1rem 0 1.5rem' }}>
      <span>
        <span style={{ display: 'inline-block', width: '12px', height: '4px', background: theme.gold, borderRadius: '1px', verticalAlign: 'middle', marginRight: '6px' }} />
        your plan
      </span>
      <span>
        <span style={{ display: 'inline-block', width: '12px', height: '4px', border: `2px solid ${theme.error}`, borderRadius: '1px', verticalAlign: 'middle', marginRight: '6px' }} />
        scenario demand
      </span>
    </div>
  </>
);

const MisesPunchline = ({ score, totalShortfall, totalSurplus, worstShortage, impactStatement }) => (
  <div style={{
    borderLeft: `3px solid ${theme.gold}`,
    paddingLeft: '16px',
    margin: '1rem 0',
    background: theme.bgDark,
    padding: '1rem 1rem 1rem 16px',
    borderRadius: '0 4px 4px 0',
  }}>
    <p style={{ fontSize: '13px', fontWeight: '600', color: theme.gold, margin: '0 0 0.75rem' }}>
      You scored {score}/100
    </p>

    {worstShortage ? (
      <div style={{ background: 'rgba(214, 63, 63, 0.15)', border: '1px solid rgba(214, 63, 63, 0.3)', borderRadius: '4px', padding: '8px 12px', marginBottom: '0.75rem' }}>
        <p style={{ fontSize: '13px', color: '#e06c6c', fontWeight: '500', margin: 0, lineHeight: '1.6' }}>
          {impactStatement}
        </p>
      </div>
    ) : (
      <div style={{ background: 'rgba(58, 170, 120, 0.15)', border: '1px solid rgba(58, 170, 120, 0.3)', borderRadius: '4px', padding: '8px 12px', marginBottom: '0.75rem' }}>
        <p style={{ fontSize: '13px', color: '#6cd6a0', fontWeight: '500', margin: 0, lineHeight: '1.6' }}>
          {impactStatement}
        </p>
      </div>
    )}

    <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '13px', color: theme.textPrimary, lineHeight: '1.8' }}>
      {/* Thresholds recalibrated 2026-07-10 against the actual score distribution
          over all 1,253,595 feasible plans under the L1 model: ~75th percentile
          (score 54) marks the top ~3% of plays, so 75 keeps "no guessing needed"
          reserved for genuinely strong plans. Median is 40, so <40 flags roughly
          the bottom half rather than nearly everyone. Judgment call, not derived
          from a single correct answer - revisit if the distribution changes. */}
      {score < 75 && (
        <li>You had to <strong>guess</strong>. The information needed to allocate correctly did not exist for you.</li>
      )}
      <li>Citizens' preferences are <strong>scattered across millions of minds</strong>. No planner can access them directly.</li>
      <li>In a market, <strong>prices aggregate that information instantly</strong>: medicine scarce → price rises → producers respond → gap closes. No instruction needed.</li>
      {score < 40 && (
        <li>In Soviet planning, misallocations weren't {totalShortfall + totalSurplus} units off across 5 goods. They were <strong>millions of tonnes off across millions of goods</strong>, year after year.</li>
      )}
      <li>Mises's insight: planning fails not because planners are incompetent, but because <strong>the required knowledge only exists inside the price system</strong>.</li>
    </ul>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

function ResultScreen({ qty, total, onReset }) {
  const { score, rows, totalShortfall, totalSurplus, worstShortage, impactStatement } = calculateResults(qty);

  return (
    <div>
      <p style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '0.25rem' }}>Step 2 of 2: Results</p>
      <h2 style={{ fontSize: '20px', color: theme.gold, margin: '0 0 1rem', fontFamily: "'EB Garamond', Georgia, serif" }}>
        Your plan vs. scenario demand
      </h2>

      <ScoreCircle score={score} />
      <ResultsTable rows={rows} total={total} totalShortfall={totalShortfall} totalSurplus={totalSurplus} />
      <BarChart rows={rows} />
      <MisesPunchline
        score={score}
        totalShortfall={totalShortfall}
        totalSurplus={totalSurplus}
        worstShortage={worstShortage}
        impactStatement={impactStatement}
      />

      <p style={{ fontSize: '14px', color: theme.textMuted, fontStyle: 'italic', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(184, 149, 90, 0.2)', maxWidth: '480px' }}>
        Every misallocation you made here, the market corrects through price signals.
        Suppressed prices don't eliminate the problem. They conceal it until the correction arrives.
      </p>

      <div style={{ marginTop: '1rem' }}>
        <button onClick={onReset} className="btn-primary">Try again</button>
      </div>
    </div>
  );
}

export default ResultScreen;
